export const LAYERED_EDIT_WARNING = "Layer split was not an exact match. Compare carefully before saving.";
export const LAYERED_EDIT_MAX_BASELINE_DIFF = 0.08;
export const LAYERED_BROWSER_FALLBACK_FONT = "Layered Inter Fallback";
export type LayerBox = { x: number; y: number; width: number; height: number };
export type LayerGradient = (
  { kind: "linear"; angle_deg: number } |
  { kind: "radial"; center_x_percent: number; center_y_percent: number }
) & { stops: { offset: number; color: string }[] };
export type LayerStyle = {
  border_radius_percent?: 50;
  border_color?: string;
  border_width_px?: number;
  background_color?: string;
  border_radius_px?: number;
  border_radius_corners_px?: [number, number, number, number];
  background_gradient?: LayerGradient;
  rotation_deg?: number;
  box_shadow?: { offset_x_px: number; offset_y_px: number; blur_px: number; spread_px: number; color: string; inset: boolean };
};
export type LayerTextStyle = {
  padding_px?: [number, number, number, number];
  font_family: string; font_size_px: number; font_weight?: number; color?: string;
  letter_spacing_px?: number; text_align?: "left" | "center" | "right"; line_height?: number;
  uppercase?: boolean; underline?: boolean; no_wrap?: boolean; italic?: boolean;
};
export type AdLayer = {
  id: string; type: "text" | "image" | "vector"; subtype?: string;
  hidden?: boolean;
  bbox: LayerBox; z_order: number; asset_path?: string; text?: string;
  style?: LayerStyle; text_style?: LayerTextStyle; image_fit?: { object_fit: "cover" | "contain" | "fill" };
};
export type LayerEdits = Record<string, { text?: string; hidden?: boolean; bbox?: LayerBox; zOrder?: number }>;
export function isLayerHidden(layer: AdLayer, edit?: LayerEdits[string]) {
  return edit?.hidden ?? layer.hidden ?? false;
}
export function layerCanBeShown(layer: AdLayer) {
  return layer.type !== "text" || !!layer.text_style?.color;
}
// CSS's default radial surface: ellipse, farthest-corner, with a percentage
// center. The normalized ellipse reaches the farthest corner at stop 1.
export function layerRadialGeometry(box: Pick<LayerBox, "width" | "height">, gradient: Extract<LayerGradient, { kind: "radial" }>) {
  const cx = box.width * gradient.center_x_percent / 100;
  const cy = box.height * gradient.center_y_percent / 100;
  return { cx, cy, rx: Math.max(cx, box.width - cx) * Math.SQRT2, ry: Math.max(cy, box.height - cy) * Math.SQRT2 };
}
// bbox remains the unrotated local surface. Rotation is clockwise around its
// center; rotated overhang clips at the canvas, not at the unrotated bbox.
export function layerRenderedBounds(box: LayerBox, rotation = 0): LayerBox {
  if (!rotation) return box;
  const angle = rotation * Math.PI / 180;
  const width = Math.abs(Math.cos(angle)) * box.width + Math.abs(Math.sin(angle)) * box.height;
  const height = Math.abs(Math.sin(angle)) * box.width + Math.abs(Math.cos(angle)) * box.height;
  return { x: box.x + (box.width - width) / 2, y: box.y + (box.height - height) / 2, width, height };
}
export function layerCanvasBounds(box: LayerBox, rotation: number | undefined, width: number, height: number): LayerBox {
  const bounds = layerRenderedBounds(box, rotation);
  const x = Math.max(0, Math.floor(bounds.x)), y = Math.max(0, Math.floor(bounds.y));
  return { x, y, width: Math.max(0, Math.min(width, Math.ceil(bounds.x + bounds.width)) - x),
    height: Math.max(0, Math.min(height, Math.ceil(bounds.y + bounds.height)) - y) };
}
export type LayerFontAsset = { url: string; family: string; weight?: string };
export type LayeredDecompositionView = {
  id: string; canvasWidth: number; canvasHeight: number; layers: AdLayer[]; fontAssets?: LayerFontAsset[];
  cached: boolean; falLatencyMs: number | null;
};
export type LayeredCanvasTelemetry = {
  moves: number; resizes: number; reorders: number; deletes: number; textEdits: number; undos: number; resets: number;
};
export type LayeredRenderResult = {
  jobId: string; image_url: string; decompositionId: string;
  baselineDiff: number; lowFidelity: boolean; fontFallback: boolean;
};
